import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import { Button, Form, Table, Modal, Spinner, Card } from "react-bootstrap";
import { ArrowLeft, Trash2, Search, FileText } from "lucide-react";

import FaturaSucessoModal from "../../components/FaturaSucesso";

const NotaDebito = () => {
  const safeText = (value) => String(value ?? "").toLowerCase();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;

  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [faturaOriginal, setFaturaOriginal] = useState(null);
  const [produtosDb, setProdutosDb] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModalProd, setShowModalProd] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [faturaGeradaId, setFaturaGeradaId] = useState(null);

  const [documentoData, setDocumentoData] = useState({
    empresa_id: empresaId,
    user_id: user?.id,
    doc_type: "ND",
    numero_fatura_origem: "",
    cliente_id: null,
    itens: [],
    observacoes: "",
    data_emissao: new Date().toISOString().split("T")[0],
    total_liquido: 0,
    total_iva: 0,
    total_geral: 0,
  });

  useEffect(() => {
    if (id && empresaId) {
      fetchDadosIniciais();
    }
  }, [id, empresaId]);

  const fetchDadosIniciais = async () => {
    try {
      setLoading(true);
      const resDoc = await api.get(`/app/documentos/${id}`);
      const fatura = resDoc.data;
      setFaturaOriginal(fatura);

      const resProd = await api.get(`/app/produtos/filter/?empresa_id=${empresaId}`);
      setProdutosDb(resProd.data);

      // CORREÇÃO: Mapear os itens existentes da fatura original
      const itensOriginais = (fatura.DocumentoItems || []).map(item => ({
        produto_id: item.produto_id,
        nome: item.nome || item.Produto?.nome,
        qtd: parseFloat(item.qtd),
        preco_unitario: parseFloat(item.preco_unitario),
        iva_percentagem: parseFloat(item.iva_percentagem || 0),
        total_item_final: parseFloat(item.total_item_final)
      }));

      setDocumentoData(prev => ({
        ...prev,
        cliente_id: fatura.cliente_id,
        numero_fatura_origem: fatura.numero,
        itens: itensOriginais // Agora os itens são carregados aqui
      }));

      // Calcula os totais com base nos itens carregados
      calculateTotals(itensOriginais);
      
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Falha ao carregar dados.", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (itens) => {
    const liq = itens.reduce((acc, cur) => acc + (parseFloat(cur.qtd || 0) * parseFloat(cur.preco_unitario || 0)), 0);
    const iva = itens.reduce((acc, cur) => acc + (parseFloat(cur.qtd || 0) * parseFloat(cur.preco_unitario || 0) * (parseFloat(cur.iva_percentagem || 0) / 100)), 0);
    setDocumentoData(prev => ({ ...prev, total_liquido: liq, total_iva: iva, total_geral: liq + iva }));
  };

  const updateItem = (index, field, value) => {
    const novosItens = [...documentoData.itens];
    novosItens[index][field] = parseFloat(value || 0);
    const subLiq = parseFloat(novosItens[index].qtd || 0) * parseFloat(novosItens[index].preco_unitario || 0);
    const subIva = subLiq * (parseFloat(novosItens[index].iva_percentagem || 0) / 100);
    novosItens[index].total_item_final = subLiq + subIva;
    setDocumentoData(prev => ({ ...prev, itens: novosItens }));
    calculateTotals(novosItens);
  };

  const handleSaveND = async () => {
    if (documentoData.itens.length === 0) return Swal.fire("Atenção", "Adicione itens à nota.", "warning");
    
    setIsProcessing(true);
    try {
      const res = await api.post("/app/documentos/create/nota-debito/", {
        ...documentoData,
        motivo_retificacao: documentoData.observacoes
      });
      setFaturaGeradaId(res.data.id);
      setShowSuccessModal(true);
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Erro na emissão.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <div className="p-5 text-center"><Spinner animation="border" /></div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4>Emitir Nota de Débito</h4>
          <small>Baseada na fatura: <b>{faturaOriginal?.numero}</b></small>
        </div>
        <Button variant="primary" onClick={() => setShowModalProd(true)}>Adicionar Produto</Button>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <Card>
            <Table hover>
              <thead>
                <tr><th>Produto</th><th>Qtd</th><th>Preço</th><th>IVA</th><th>Total</th><th></th></tr>
              </thead>
              <tbody>
                {documentoData.itens.map((item, index) => (
                  <tr key={index}>
                    <td>{item.nome}</td>
                    <td><Form.Control type="number" size="sm" value={item.qtd} onChange={(e) => updateItem(index, 'qtd', e.target.value)} /></td>
                    <td><Form.Control type="number" size="sm" value={item.preco_unitario} onChange={(e) => updateItem(index, 'preco_unitario', e.target.value)} /></td>
                    <td>{item.iva_percentagem}%</td>
                    <td>{item.total_item_final.toFixed(2)} Kz</td>
                    <td><Button variant="link" className="text-danger" onClick={() => { const f = documentoData.itens.filter((_, i) => i !== index); setDocumentoData({...documentoData, itens: f}); calculateTotals(f); }}><Trash2 size={16} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>

        <div className="col-lg-4">
          <Card className="p-3 mb-3">
            <Form.Label>Motivo da Retificação</Form.Label>
            <Form.Control as="textarea" value={documentoData.observacoes} onChange={(e) => setDocumentoData({...documentoData, observacoes: e.target.value})} />
          </Card>
          <Card className="p-3 text-center bg-primary text-white">
            <h5>Total Débito</h5>
            <h2>{documentoData.total_geral.toFixed(2)} Kz</h2>
            <Button variant="light" className="w-100 mt-2" onClick={handleSaveND} disabled={isProcessing}>
              {isProcessing ? <Spinner size="sm" /> : "Confirmar"}
            </Button>
          </Card>
        </div>
      </div>

      <Modal show={showModalProd} onHide={() => setShowModalProd(false)}>
        <Modal.Header closeButton><Modal.Title>Adicionar Produto</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Control className="mb-3" placeholder="Pesquisar..." onChange={(e) => setSearchTerm(e.target.value)} />
          {produtosDb.filter(p => safeText(p?.nome).includes(safeText(searchTerm))).map(p => (
            <div key={p.id} className="d-flex justify-content-between p-2 border-bottom">
              <span>{p.nome}</span>
              <Button size="sm" onClick={() => {
                const preco = parseFloat(p.preco || p.preco_venda || 0);
                const iva = parseFloat(p.iva || 0);
                const novo = { produto_id: p.id, nome: p.nome, qtd: 1, preco_unitario: preco, iva_percentagem: iva, total_item_final: preco * (1 + (iva / 100)) };
                const lista = [...documentoData.itens, novo];
                setDocumentoData({...documentoData, itens: lista});
                calculateTotals(lista);
                setShowModalProd(false);
              }}>Adicionar</Button>
            </div>
          ))}
        </Modal.Body>
      </Modal>

      {showSuccessModal && <FaturaSucessoModal faturaId={faturaGeradaId} onClose={() => navigate("/faturacao/documentos-emitidos")} />}
    </div>
  );
};

export default NotaDebito;
